import { useEffect, useState } from "react";
import { EVENTS } from "@cookies/shared/catalog/display";
import { mutate, useSnapshot } from "../data/store";
import { Panel, Toggle } from "../components/ui";
import { usePrefs } from "../contexts/PrefsContext";
import { getStoredUser } from "../auth/session";
import { fmtClock } from "../game/format";
import { applyMusicSetting, applySfxSetting, unlockAudioOnce } from "../game/music";

export function GoldenCookieOverlay({ endsAt, onClaim, onClose }: { endsAt?: number; onClaim?: () => void; onClose: () => void }) {
  const [now, setNow] = useState(0);
  useEffect(() => {
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(t);
  }, []);
  const left = Math.max(0, (endsAt ?? now) - now);
  useEffect(() => {
    if (left <= 0) onClose();
  }, [left, onClose]);
  useEffect(() => {
    const isSpace = (e: KeyboardEvent): boolean => e.code === "Space" || e.key === " " || e.key === "Spacebar"
    const isEnter = (e: KeyboardEvent): boolean => e.key === "Enter"
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose()
        return
      }
      if ((isSpace(e) || isEnter(e)) && onClaim) {
        e.preventDefault()
        try {
          if (typeof (e as unknown as { stopPropagation?: () => void }).stopPropagation === "function") {
            ;(e as unknown as { stopPropagation: () => void }).stopPropagation()
          }
        } catch {
        }
        onClaim()
      }
    }
    window.addEventListener("keydown", onKey, true)
    return () => window.removeEventListener("keydown", onKey, true)
  }, [onClose, onClaim])
  return (
    <div className="cb-event-bg" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="cb-event" role="dialog" aria-modal="true" aria-label="Golden Cookie event">
        <button type="button" className="cb-x" onClick={onClose} aria-label="Close golden cookie">✕</button>
        <h1 className="cb-gold-title">Golden Cookie!</h1>
        <div className="cb-gold-sub">One tap redeems it — then keep clicking the big cookie for 5x during the 10s frenzy.</div>
        <div style={{ position: "relative", display: "inline-block" }}>
          <span className="cb-spark" style={{ left: -10, top: 6 }} aria-hidden="true">✨</span>
          <span className="cb-spark" style={{ right: -6, top: 22 }} aria-hidden="true">✨</span>
          <span className="cb-spark" style={{ left: 12, bottom: -6 }} aria-hidden="true">⭐</span>
          <button type="button" className="cb-gold-cookie" onClick={onClaim} aria-label="Catch golden cookie, one tap redeems">
            🍪
          </button>
        </div>
        <div><div className="cb-timer" role="status" aria-live="polite">⏱ {fmtClock(left)} · one tap to redeem</div></div>
        <p className="dim small" style={{ color: "#ffe9a8" }}>5x cookies for 10s · Auto-fades when the timer ends</p>
      </div>
    </div>
  );
}

export function EventList() {
  return (
    <div className="cb-page">
      <Panel className="hl center"><h2 className="cb-h">🎪 Events</h2><p className="dim small" style={{ margin: 0 }}>Name · time left · objective · reward</p></Panel>
      {EVENTS.map((e) => (
        <div key={e.id} className="cb-card">
          <span className="cb-card-ico" aria-hidden="true">{e.icon}</span>
          <div className="cb-card-body"><b>{e.name}</b><small>{e.desc}</small><small className="dim">{e.reward}</small></div>
        </div>
      ))}
    </div>
  );
}

function particleLabel(d: string): string {
  if (d === "off") return "Off";
  if (d === "low") return "Low";
  return "High";
}

export default function SettingsPage() {
  const { state } = useSnapshot();
  const s = state.settings;
  const user = getStoredUser();
  const prefs = usePrefs();
  const set = (patch: Partial<typeof s>) => {
    mutate((st) => ({ ...st, settings: { ...st.settings, ...patch } }))
    if (patch.music !== undefined) applyMusicSetting(patch.music)
    if (patch.sfx !== undefined) applySfxSetting(patch.sfx)
  };
  const toggleMusic = () => {
    const next = !s.music
    if (next) unlockAudioOnce()
    set({ music: next })
  }
  const toggleSfx = () => {
    const next = !s.sfx
    if (next) unlockAudioOnce()
    set({ sfx: next })
  };
  return (
    <div className="cb-page">
      <Panel className="hl center"><h2 className="cb-h">⚙ Settings</h2></Panel>
      <Panel>
        <h3 className="cb-sec on-light">🔊 Audio</h3>
        <div className="cb-set"><span>Music</span><Toggle on={s.music} label="Music" onClick={toggleMusic} /></div>
        <div className="cb-set"><span>SFX</span><Toggle on={s.sfx} label="SFX" onClick={toggleSfx} /></div>
      </Panel>
      <Panel>
        <h3 className="cb-sec on-light">✨ Visuals</h3>
        <div className="cb-set"><span>Particles</span>
          <select
            className="cb-select"
            value={particleLabel(prefs.density)}
            aria-label="Particle density"
            onChange={(e) => {
              const v = e.target.value;
              prefs.setDensity(v === "Off" ? "off" : v === "Low" ? "low" : "high");
              set({ particles: v });
            }}
          >
            <option>Low</option><option>High</option><option>Off</option>
          </select></div>
        <div className="cb-set"><span>Reduced Motion</span><Toggle
          on={prefs.reduced}
          label="Reduced Motion"
          onClick={() => {
            prefs.toggleReduced();
            set({ reducedMotion: !prefs.reduced });
          }}
        /></div>
      </Panel>
      <Panel>
        <h3 className="cb-sec on-light">♿ Accessibility</h3>
        <div className="cb-set"><span>High Contrast</span><Toggle on={s.highContrast} label="High Contrast" onClick={() => set({ highContrast: !s.highContrast })} /></div>
        <div className="cb-set"><span>Space to click cookie</span><Toggle on={prefs.spaceClick} label="Space to click cookie" onClick={() => prefs.toggleSpaceClick()} /></div>
        <small className="dim">Off by default so Space keeps scrolling. When on, each discrete Space press bakes once.</small>
        <div className="cb-set"><span>Text Size</span>
          <select className="cb-select" value={s.textSize} aria-label="Text size" onChange={(e) => set({ textSize: e.target.value })}>
            <option>Normal</option><option>Large</option><option>XL</option>
          </select></div>
      </Panel>
      <Panel>
        <h3 className="cb-sec on-light">👤 Account</h3>
        <div className="cb-set"><span>🎮 Discord · {user ? `@${user.username}` : "Not signed in"}</span><button type="button" className="cb-buy ghost">Manage</button></div>
        <small className="dim">{user ? `Signed in as ${user.username}` : "Sign in with Discord to join a bakery."}</small>
      </Panel>
      <Panel>
        <h3 className="cb-sec on-light">🎮 Discord</h3>
        <div className="cb-set"><span>Share Discord status</span><Toggle on={s.discordPresence !== false} label="Share Discord status" onClick={() => set({ discordPresence: !(s.discordPresence !== false) })} /></div>
        <small className="dim">Shows your bakery (cookies, level, CPS) as Discord Rich Presence while playing in Discord. Off clears it.</small>
      </Panel>
    </div>
  );
}
